// AI SDK Wrapper with OpenTelemetry Instrumentation
import { AgentBillTracer } from './tracer';
import { AgentBillConfig, TraceContext, TrackSignalParams } from './types';

interface ValidationResponse {
  allowed: boolean;
  reason?: string;
  tier?: string;
  mode?: string;
  [key: string]: any;
}

export class AgentBillWrapper {
  private tracer: AgentBillTracer;
  private config: AgentBillConfig;

  constructor(config: AgentBillConfig) {
    this.config = {
      ...config,
      baseUrl: config.baseUrl || 'https://bgwyprqxtdreuutzpbgw.supabase.co'
    };
    this.tracer = new AgentBillTracer(this.config);
  }

  /**
   * Wrap an OpenAI client instance
   */
  wrapOpenAI<T extends object>(client: T): T {
    const self = this;

    return new Proxy(client, {
      get(target: any, prop: string | symbol) {
        const original = target[prop];

        // Intercept chat.completions.create
        if (prop === 'chat') {
          return new Proxy(original, {
            get(chatTarget: any, chatProp: string | symbol) {
              if (chatProp === 'completions') {
                return new Proxy(chatTarget[chatProp], {
                  get(completionsTarget: any, completionsProp: string | symbol) {
                    if (completionsProp === 'create') {
                      return async function(params: any, options?: any) {
                        return self.instrumentOpenAICall(
                          completionsTarget[completionsProp].bind(completionsTarget),
                          params,
                          options
                        );
                      };
                    }
                    return completionsTarget[completionsProp];
                  }
                });
              }
              return chatTarget[chatProp];
            }
          });
        }

        // Intercept embeddings.create
        if (prop === 'embeddings') {
          return new Proxy(original, {
            get(embeddingsTarget: any, embeddingsProp: string | symbol) {
              if (embeddingsProp === 'create') {
                return async function(params: any) {
                  return self.instrumentOpenAIEmbeddings(
                    embeddingsTarget[embeddingsProp].bind(embeddingsTarget),
                    params
                  );
                };
              }
              return embeddingsTarget[embeddingsProp];
            }
          });
        }

        // Intercept images.generate
        if (prop === 'images') {
          return new Proxy(original, {
            get(imagesTarget: any, imagesProp: string | symbol) {
              if (imagesProp === 'generate') {
                return async function(params: any) {
                  return self.instrumentOpenAIImages(
                    imagesTarget[imagesProp].bind(imagesTarget),
                    params
                  );
                };
              }
              return imagesTarget[imagesProp];
            }
          });
        }

        // Intercept audio.transcriptions.create and audio.speech.create
        if (prop === 'audio') {
          return new Proxy(original, {
            get(audioTarget: any, audioProp: string | symbol) {
              if (audioProp === 'transcriptions') {
                return new Proxy(audioTarget[audioProp], {
                  get(transcriptionsTarget: any, transcriptionsProp: string | symbol) {
                    if (transcriptionsProp === 'create') {
                      return async function(params: any) {
                        return self.instrumentOpenAIAudioTranscription(
                          transcriptionsTarget[transcriptionsProp].bind(transcriptionsTarget),
                          params
                        );
                      };
                    }
                    return transcriptionsTarget[transcriptionsProp];
                  }
                });
              }
              if (audioProp === 'speech') {
                return new Proxy(audioTarget[audioProp], {
                  get(speechTarget: any, speechProp: string | symbol) {
                    if (speechProp === 'create') {
                      return async function(params: any) {
                        return self.instrumentOpenAIAudioSpeech(
                          speechTarget[speechProp].bind(speechTarget),
                          params
                        );
                      };
                    }
                    return speechTarget[speechProp];
                  }
                });
              }
              return audioTarget[audioProp];
            }
          });
        }

        // Intercept moderations.create
        if (prop === 'moderations') {
          return new Proxy(original, {
            get(moderationsTarget: any, moderationsProp: string | symbol) {
              if (moderationsProp === 'create') {
                return async function(params: any) {
                  return self.instrumentOpenAIModerations(
                    moderationsTarget[moderationsProp].bind(moderationsTarget),
                    params
                  );
                };
              }
              return moderationsTarget[moderationsProp];
            }
          });
        }

        return original;
      }
    });
  }

  private async instrumentOpenAICall(originalFn: Function, params: any, options?: any) {
    const traceContext = this.tracer.startSpan('openai.chat.completions.create');
    const startTime = Date.now();
    
    // Extract event_name from options if provided
    const eventName = options?.agentbill?.event_name || 'ai_request';

    try {
      // Set span attributes
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'unknown');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'unknown');

      if (params.temperature !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.temperature', params.temperature);
      }
      if (params.max_tokens !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.max_tokens', params.max_tokens);
      }

      // PHASE 1: Validate with Cost Guard Router (tier-based routing)
      if (this.config.customerId && (this.config.dailyBudget || this.config.monthlyBudget)) {
        const messages = params.messages || [];

        try {
          const validateResponse = await fetch(`${this.config.baseUrl}/functions/v1/ai-cost-guard-router`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              api_key: this.config.apiKey,
              customer_id: this.config.customerId,
              model: params.model,
              messages
            })
          });

          const validation = await validateResponse.json() as ValidationResponse;
          
          if (!validation.allowed) {
            const error: any = new Error(validation.reason || 'Request blocked by Cost Guard');
            error.code = validation.reason?.includes('budget') ? 'BUDGET_EXCEEDED' : 
                         validation.reason?.includes('rate') ? 'RATE_LIMIT_EXCEEDED' : 
                         'POLICY_VIOLATION';
            error.details = validation;
            throw error;
          }

          // Log tier-based features if in debug mode
          if (this.config.debug && validation.tier) {
            console.log(`[AgentBill] Tier: ${validation.tier}, Mode: ${validation.mode}`);
          }
        } catch (error: any) {
          if (error.code) throw error; // Re-throw Cost Guard errors
          // If validation fails, log but continue (fail-open for availability)
          console.warn('[AgentBill] Cost Guard router failed, proceeding with request:', error.message);
        }
      }

      // PHASE 2: Execute the original API call with client's credentials
      const response = await originalFn(params);

      // Calculate latency
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      // Extract token usage from response
      if (response.usage) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.prompt_tokens', response.usage.prompt_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.completion_tokens', response.usage.completion_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.total_tokens', response.usage.total_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.prompt_tokens', response.usage.prompt_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.completion_tokens', response.usage.completion_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.total_tokens', response.usage.total_tokens || 0);

        // PHASE 3: Track usage with Cost Guard
        if (this.config.customerId) {
          const actualCost = this.estimateCost(
            params.model,
            response.usage.prompt_tokens || 0,
            response.usage.completion_tokens || 0
          );

          // Track asynchronously (don't block response)
          this.trackUsage({
            model: params.model,
            provider: 'openai',
            input_tokens: response.usage.prompt_tokens || 0,
            output_tokens: response.usage.completion_tokens || 0,
            total_tokens: response.usage.total_tokens || 0,
            cost: actualCost,
            latency_ms: latencyMs,
            success: true,
            event_name: eventName
          }).catch(err => console.warn('[AgentBill] Failed to track usage:', err.message));
        }
      }

      // Set success status
      this.tracer.setSpanStatus(traceContext.spanId, 0); // 0 = OK
      this.tracer.endSpan(traceContext.spanId);

      return response;
    } catch (error) {
      // Track failed request
      if (this.config.customerId) {
        this.trackUsage({
          model: params.model,
          provider: 'openai',
          success: false,
          error_type: error instanceof Error ? error.constructor.name : 'unknown',
          error_message: error instanceof Error ? error.message : String(error),
          latency_ms: Date.now() - startTime,
          event_name: eventName
        }).catch(err => console.warn('[AgentBill] Failed to track error:', err.message));
      }

      // Set error status
      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  private estimateCost(model: string, inputTokens: number, outputTokens: number): number {
    // Simple cost estimation - should match backend logic
    const pricing: Record<string, { input: number, output: number }> = {
      'gpt-4o': { input: 0.0025, output: 0.01 },
      'gpt-4o-mini': { input: 0.00015, output: 0.0006 },
      'gpt-4-turbo': { input: 0.01, output: 0.03 },
      'gpt-3.5-turbo': { input: 0.0005, output: 0.0015 }
    };

    const rates = pricing[model] || pricing['gpt-3.5-turbo'];
    return ((inputTokens / 1000) * rates.input) + ((outputTokens / 1000) * rates.output);
  }

  private async trackUsage(data: any): Promise<void> {
    try {
      await fetch(`${this.config.baseUrl}/functions/v1/track-ai-usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          api_key: this.config.apiKey,
          customer_id: this.config.customerId,
          agent_id: this.config.agentId,
          event_name: data.event_name || 'ai_request',
          ...data
        })
      });
    } catch (error) {
      // Silent fail - tracking shouldn't break user experience
      if (this.config.debug) {
        console.error('[AgentBill] Tracking failed:', error);
      }
    }
  }

  private async instrumentOpenAIEmbeddings(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('openai.embeddings.create');
    const startTime = Date.now();

    try {
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'unknown');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'unknown');

      const response = await originalFn(params);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      if (response.usage) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.prompt_tokens', response.usage.prompt_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.total_tokens', response.usage.total_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.prompt_tokens', response.usage.prompt_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.total_tokens', response.usage.total_tokens || 0);
      }

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);
      return response;
    } catch (error) {
      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  private async instrumentOpenAIImages(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('openai.images.generate');
    const startTime = Date.now();

    try {
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'dall-e-3');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'dall-e-3');
      
      if (params.size) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'image.size', params.size);
      }
      if (params.quality) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'image.quality', params.quality);
      }

      const response = await originalFn(params);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);
      return response;
    } catch (error) {
      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  private async instrumentOpenAIAudioTranscription(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('openai.audio.transcriptions.create');
    const startTime = Date.now();

    try {
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'whisper-1');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'whisper-1');

      const response = await originalFn(params);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);
      return response;
    } catch (error) {
      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  private async instrumentOpenAIAudioSpeech(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('openai.audio.speech.create');
    const startTime = Date.now();

    try {
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'tts-1');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'tts-1');
      
      if (params.voice) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'audio.voice', params.voice);
      }

      const response = await originalFn(params);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);
      return response;
    } catch (error) {
      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  private async instrumentOpenAIModerations(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('openai.moderations.create');
    const startTime = Date.now();

    try {
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'text-moderation-latest');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'text-moderation-latest');

      const response = await originalFn(params);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);
      return response;
    } catch (error) {
      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  /**
   * Wrap an Anthropic client instance
   */
  wrapAnthropic<T extends object>(client: T): T {
    const self = this;

    return new Proxy(client, {
      get(target: any, prop: string | symbol) {
        const original = target[prop];

        // Intercept messages.create
        if (prop === 'messages') {
          return new Proxy(original, {
            get(messagesTarget: any, messagesProp: string | symbol) {
              if (messagesProp === 'create') {
                return async function(params: any) {
                  return self.instrumentAnthropicCall(
                    messagesTarget[messagesProp].bind(messagesTarget),
                    params
                  );
                };
              }
              return messagesTarget[messagesProp];
            }
          });
        }

        return original;
      }
    });
  }

  private async instrumentAnthropicCall(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('anthropic.messages.create');
    const startTime = Date.now();

    try {
      // Set span attributes
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'anthropic');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'unknown');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'anthropic');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'unknown');

      if (params.temperature !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.temperature', params.temperature);
      }
      if (params.max_tokens !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.max_tokens', params.max_tokens);
      }

      // PHASE 1: Validate with Cost Guard Router (tier-based routing)
      if (this.config.customerId && (this.config.dailyBudget || this.config.monthlyBudget)) {
        const messages = params.messages || [];

        try {
          const validateResponse = await fetch(`${this.config.baseUrl}/functions/v1/ai-cost-guard-router`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              api_key: this.config.apiKey,
              customer_id: this.config.customerId,
              model: params.model,
              messages
            })
          });

          const validation = await validateResponse.json() as ValidationResponse;
          
          if (!validation.allowed) {
            const error: any = new Error(validation.reason || 'Request blocked by Cost Guard');
            error.code = validation.reason?.includes('budget') ? 'BUDGET_EXCEEDED' : 
                         validation.reason?.includes('rate') ? 'RATE_LIMIT_EXCEEDED' : 
                         'POLICY_VIOLATION';
            error.details = validation;
            throw error;
          }
        } catch (error: any) {
          if (error.code) throw error;
          console.warn('[AgentBill] Cost Guard validation failed, proceeding with request:', error.message);
        }
      }

      // PHASE 2: Execute the original API call
      const response = await originalFn(params);

      // Calculate latency
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      // Extract token usage from response
      if (response.usage) {
        const inputTokens = response.usage.input_tokens || 0;
        const outputTokens = response.usage.output_tokens || 0;
        
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.prompt_tokens', inputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.completion_tokens', outputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.total_tokens', inputTokens + outputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.prompt_tokens', inputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.completion_tokens', outputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.total_tokens', inputTokens + outputTokens);

        // PHASE 3: Track usage with Cost Guard
        if (this.config.customerId) {
          const actualCost = this.estimateCost(params.model, inputTokens, outputTokens);
          this.trackUsage({
            model: params.model,
            provider: 'anthropic',
            input_tokens: inputTokens,
            output_tokens: outputTokens,
            total_tokens: inputTokens + outputTokens,
            cost: actualCost,
            latency_ms: latencyMs,
            success: true
          }).catch(err => console.warn('[AgentBill] Failed to track usage:', err.message));
        }
      }

      // Set success status
      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);

      return response;
    } catch (error) {
      // Track failed request
      if (this.config.customerId) {
        this.trackUsage({
          model: params.model,
          provider: 'anthropic',
          success: false,
          error_type: error instanceof Error ? error.constructor.name : 'unknown',
          error_message: error instanceof Error ? error.message : String(error),
          latency_ms: Date.now() - startTime
        }).catch(err => console.warn('[AgentBill] Failed to track error:', err.message));
      }

      // Set error status
      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  /**
   * Wrap an AWS Bedrock client instance
   */
  wrapBedrock<T extends object>(client: T): T {
    const self = this;

    return new Proxy(client, {
      get(target: any, prop: string | symbol) {
        const original = target[prop];

        // Intercept invokeModel calls
        if (prop === 'invokeModel' || prop === 'send') {
          return async function(command: any) {
            return self.instrumentBedrockCall(
              original.bind(target),
              command
            );
          };
        }

        return original;
      }
    });
  }

  private async instrumentBedrockCall(originalFn: Function, command: any) {
    const traceContext = this.tracer.startSpan('bedrock.invokeModel');
    const startTime = Date.now();

    try {
      // Extract model from command
      const modelId = command.modelId || command.input?.modelId || 'unknown';
      
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'bedrock');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', modelId);
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'bedrock');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', modelId);

      // PHASE 1: Validate with Cost Guard Router (tier-based routing)
      if (this.config.customerId && (this.config.dailyBudget || this.config.monthlyBudget)) {
        const bodyStr = command.body || command.input?.body || '{}';
        const body = JSON.parse(bodyStr);
        const messages = body.messages || body.prompt || '';

        try {
          const validateResponse = await fetch(`${this.config.baseUrl}/functions/v1/ai-cost-guard-router`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              api_key: this.config.apiKey,
              customer_id: this.config.customerId,
              model: modelId,
              messages
            })
          });

          const validation = await validateResponse.json() as ValidationResponse;
          
          if (!validation.allowed) {
            const error: any = new Error(validation.reason || 'Request blocked by Cost Guard');
            error.code = 'BUDGET_EXCEEDED';
            throw error;
          }
        } catch (error: any) {
          if (error.code) throw error;
          console.warn('[AgentBill] Cost Guard validation failed, proceeding with request:', error.message);
        }
      }

      // PHASE 2: Execute API call
      const response = await originalFn(command);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      // Parse response body for token usage
      if (response.body) {
        const bodyText = await response.body.transformToString();
        const bodyJson = JSON.parse(bodyText);
        
        // Different Bedrock models have different response formats
        if (bodyJson.usage) {
          const inputTokens = bodyJson.usage.input_tokens || 0;
          const outputTokens = bodyJson.usage.output_tokens || 0;
          
          this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.prompt_tokens', inputTokens);
          this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.completion_tokens', outputTokens);
          this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.total_tokens', inputTokens + outputTokens);
          this.tracer.setSpanAttribute(traceContext.spanId, 'ai.prompt_tokens', inputTokens);
          this.tracer.setSpanAttribute(traceContext.spanId, 'ai.completion_tokens', outputTokens);
          this.tracer.setSpanAttribute(traceContext.spanId, 'ai.total_tokens', inputTokens + outputTokens);

          // PHASE 3: Track usage
          if (this.config.customerId) {
            const actualCost = this.estimateCost(modelId, inputTokens, outputTokens);
            this.trackUsage({
              model: modelId,
              provider: 'bedrock',
              input_tokens: inputTokens,
              output_tokens: outputTokens,
              total_tokens: inputTokens + outputTokens,
              cost: actualCost,
              latency_ms: latencyMs,
              success: true
            }).catch(err => console.warn('[AgentBill] Failed to track usage:', err.message));
          }
        }
      }

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);

      return response;
    } catch (error) {
      // Track failed request
      if (this.config.customerId) {
        const modelId = command.modelId || command.input?.modelId || 'unknown';
        this.trackUsage({
          model: modelId,
          provider: 'bedrock',
          success: false,
          error_type: error instanceof Error ? error.constructor.name : 'unknown',
          error_message: error instanceof Error ? error.message : String(error),
          latency_ms: Date.now() - startTime
        }).catch(err => console.warn('[AgentBill] Failed to track error:', err.message));
      }

      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  /**
   * Wrap an Azure OpenAI client instance
   */
  wrapAzureOpenAI<T extends object>(client: T): T {
    const self = this;

    return new Proxy(client, {
      get(target: any, prop: string | symbol) {
        const original = target[prop];

        // Azure OpenAI uses the same structure as OpenAI
        if (prop === 'chat') {
          return new Proxy(original, {
            get(chatTarget: any, chatProp: string | symbol) {
              if (chatProp === 'completions') {
                return new Proxy(chatTarget[chatProp], {
                  get(completionsTarget: any, completionsProp: string | symbol) {
                    if (completionsProp === 'create') {
                      return async function(params: any) {
                        return self.instrumentAzureOpenAICall(
                          completionsTarget[completionsProp].bind(completionsTarget),
                          params
                        );
                      };
                    }
                    return completionsTarget[completionsProp];
                  }
                });
              }
              return chatTarget[chatProp];
            }
          });
        }

        return original;
      }
    });
  }

  private async instrumentAzureOpenAICall(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('azure_openai.chat.completions.create');
    const startTime = Date.now();

    try {
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'azure_openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'unknown');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'azure_openai');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'unknown');

      if (params.temperature !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.temperature', params.temperature);
      }
      if (params.max_tokens !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.max_tokens', params.max_tokens);
      }

      // PHASE 1: Validate with Cost Guard Router (tier-based routing)
      if (this.config.customerId && (this.config.dailyBudget || this.config.monthlyBudget)) {
        const messages = params.messages || [];

        try {
          const validateResponse = await fetch(`${this.config.baseUrl}/functions/v1/ai-cost-guard-router`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              api_key: this.config.apiKey,
              customer_id: this.config.customerId,
              model: params.model,
              messages
            })
          });

          const validation = await validateResponse.json() as ValidationResponse;
          
          if (!validation.allowed) {
            const error: any = new Error(validation.reason || 'Request blocked by Cost Guard');
            error.code = 'BUDGET_EXCEEDED';
            throw error;
          }
        } catch (error: any) {
          if (error.code) throw error;
          console.warn('[AgentBill] Cost Guard validation failed, proceeding with request:', error.message);
        }
      }

      // PHASE 2: Execute API call
      const response = await originalFn(params);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      if (response.usage) {
        const inputTokens = response.usage.prompt_tokens || 0;
        const outputTokens = response.usage.completion_tokens || 0;
        
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.prompt_tokens', inputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.completion_tokens', outputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.total_tokens', response.usage.total_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.prompt_tokens', inputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.completion_tokens', outputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.total_tokens', response.usage.total_tokens || 0);

        // PHASE 3: Track usage
        if (this.config.customerId) {
          const actualCost = this.estimateCost(params.model, inputTokens, outputTokens);
          this.trackUsage({
            model: params.model,
            provider: 'azure_openai',
            input_tokens: inputTokens,
            output_tokens: outputTokens,
            total_tokens: response.usage.total_tokens || 0,
            cost: actualCost,
            latency_ms: latencyMs,
            success: true
          }).catch(err => console.warn('[AgentBill] Failed to track usage:', err.message));
        }
      }

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);

      return response;
    } catch (error) {
      // Track failed request
      if (this.config.customerId) {
        this.trackUsage({
          model: params.model,
          provider: 'azure_openai',
          success: false,
          error_type: error instanceof Error ? error.constructor.name : 'unknown',
          error_message: error instanceof Error ? error.message : String(error),
          latency_ms: Date.now() - startTime
        }).catch(err => console.warn('[AgentBill] Failed to track error:', err.message));
      }

      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  /**
   * Wrap a Mistral client instance
   */
  wrapMistral<T extends object>(client: T): T {
    const self = this;

    return new Proxy(client, {
      get(target: any, prop: string | symbol) {
        const original = target[prop];

        // Intercept chat.complete or chat.stream
        if (prop === 'chat') {
          return new Proxy(original, {
            get(chatTarget: any, chatProp: string | symbol) {
              if (chatProp === 'complete' || chatProp === 'stream') {
                return async function(params: any) {
                  return self.instrumentMistralCall(
                    chatTarget[chatProp].bind(chatTarget),
                    params
                  );
                };
              }
              return chatTarget[chatProp];
            }
          });
        }

        return original;
      }
    });
  }

  private async instrumentMistralCall(originalFn: Function, params: any) {
    const traceContext = this.tracer.startSpan('mistral.chat.complete');
    const startTime = Date.now();

    try {
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.system', 'mistral');
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.model', params.model || 'unknown');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.provider', 'mistral');
      this.tracer.setSpanAttribute(traceContext.spanId, 'ai.model', params.model || 'unknown');

      if (params.temperature !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.temperature', params.temperature);
      }
      if (params.max_tokens !== undefined) {
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.request.max_tokens', params.max_tokens);
      }

      // PHASE 1: Validate with Cost Guard Router (tier-based routing)
      if (this.config.customerId && (this.config.dailyBudget || this.config.monthlyBudget)) {
        const messages = params.messages || [];

        try {
          const validateResponse = await fetch(`${this.config.baseUrl}/functions/v1/ai-cost-guard-router`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              api_key: this.config.apiKey,
              customer_id: this.config.customerId,
              model: params.model,
              messages
            })
          });

          const validation = await validateResponse.json() as ValidationResponse;
          
          if (!validation.allowed) {
            const error: any = new Error(validation.reason || 'Request blocked by Cost Guard');
            error.code = 'BUDGET_EXCEEDED';
            throw error;
          }
        } catch (error: any) {
          if (error.code) throw error;
          console.warn('[AgentBill] Cost Guard validation failed, proceeding with request:', error.message);
        }
      }

      // PHASE 2: Execute API call
      const response = await originalFn(params);
      const latencyMs = Date.now() - startTime;
      this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.response.latency_ms', latencyMs);

      // Mistral uses usage object similar to OpenAI
      if (response.usage) {
        const inputTokens = response.usage.prompt_tokens || 0;
        const outputTokens = response.usage.completion_tokens || 0;
        
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.prompt_tokens', inputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.completion_tokens', outputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'gen_ai.usage.total_tokens', response.usage.total_tokens || 0);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.prompt_tokens', inputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.completion_tokens', outputTokens);
        this.tracer.setSpanAttribute(traceContext.spanId, 'ai.total_tokens', response.usage.total_tokens || 0);

        // PHASE 3: Track usage
        if (this.config.customerId) {
          const actualCost = this.estimateCost(params.model, inputTokens, outputTokens);
          this.trackUsage({
            model: params.model,
            provider: 'mistral',
            input_tokens: inputTokens,
            output_tokens: outputTokens,
            total_tokens: response.usage.total_tokens || 0,
            cost: actualCost,
            latency_ms: latencyMs,
            success: true
          }).catch(err => console.warn('[AgentBill] Failed to track usage:', err.message));
        }
      }

      this.tracer.setSpanStatus(traceContext.spanId, 0);
      this.tracer.endSpan(traceContext.spanId);

      return response;
    } catch (error) {
      // Track failed request
      if (this.config.customerId) {
        this.trackUsage({
          model: params.model,
          provider: 'mistral',
          success: false,
          error_type: error instanceof Error ? error.constructor.name : 'unknown',
          error_message: error instanceof Error ? error.message : String(error),
          latency_ms: Date.now() - startTime
        }).catch(err => console.warn('[AgentBill] Failed to track error:', err.message));
      }

      this.tracer.setSpanStatus(traceContext.spanId, 2, error instanceof Error ? error.message : 'Unknown error');
      this.tracer.setSpanAttribute(traceContext.spanId, 'error', true);
      this.tracer.setSpanAttribute(traceContext.spanId, 'error.message', error instanceof Error ? error.message : String(error));
      this.tracer.endSpan(traceContext.spanId);
      throw error;
    }
  }

  /**
   * Manually track a custom signal with revenue and optional OTEL correlation
   * 
   * @param params Signal parameters including optional trace_id and span_id for OTEL correlation
   * 
   * @example
   * // Basic tracking
   * await agentBill.trackSignal({
   *   event_name: 'user_signup',
   *   revenue: 99.99,
   *   customer_id: 'cust_123'
   * });
   * 
   * // With OTEL correlation for cost reconciliation
   * const traceContext = agentBill.tracer.startSpan('ai_completion');
   * // ... make AI call ...
   * await agentBill.trackSignal({
   *   event_name: 'ai_request',
   *   revenue: 5.00,
   *   trace_id: traceContext.traceId,  // Optional
   *   span_id: traceContext.spanId     // Optional
   * });
   */
  async trackSignal(params: TrackSignalParams): Promise<{ success: boolean; error?: string }> {
    try {
      const endpoint = this.config.baseUrl 
        ? `${this.config.baseUrl}/record-signals`
        : 'https://bgwyprqxtdreuutzpbgw.supabase.co/functions/v1/record-signals';

      // Auto-fill customer_id or customer_external_id from config if not provided
      const payload = { ...params };
      if (!payload.customer_id && !payload.customer_external_id && this.config.customerId) {
        // Check if it's a UUID format - send as customer_id, else customer_external_id
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const isUuid = uuidRegex.test(this.config.customerId);
        if (isUuid) {
          payload.customer_id = this.config.customerId;
        } else {
          payload.customer_external_id = this.config.customerId;
        }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json() as { error?: string; message?: string };

      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || data.message || `HTTP ${response.status}` 
        };
      }

      if (this.config.debug) {
        const traceInfo = params.trace_id ? ` (trace: ${params.trace_id})` : '';
        console.log(`[AgentBill] Signal tracked: ${params.event_name}${traceInfo}`);
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  async flush() {
    await this.tracer.flush();
  }
}
